import 'swiper/css';
import 'swiper/css/navigation';

import { Key, ReactNode, Ref, forwardRef, useRef, useState } from 'react';

import clsx from 'clsx';
import SwiperType, { Navigation } from 'swiper';
import { Swiper, SwiperProps, SwiperRef, SwiperSlide } from 'swiper/react';

import { trackElementInteraction } from '@/common/analytics/gtm/shared/trackElementInteraction';
import ChevronIcon from '@/common/icons/Chevron/ChevronIcon';
import { IconSize } from '@/common/icons/icon.interface';
import COLORS from '@/styles/COLORS';

import styles from './slider.module.scss';

export interface SliderProps<SlideType> {
  slides: SlideType[];
  renderSlide: (slide: SlideType, index: number) => ReactNode;
  keyExtractor: (slide: SlideType) => Key;
  options?: SwiperProps;
  children?: ReactNode;
  slideClassName?: string;
  metadata?: {
    elementVariant?: string;
  };
  onPrevClick?: () => void;
  onNextClick?: () => void;
}

const SliderWithoutRef = <SlideType,>(
  {
    options,
    renderSlide,
    keyExtractor,
    slides,
    children,
    slideClassName = '',
    metadata,
    onPrevClick,
    onNextClick,
  }: SliderProps<SlideType>,
  ref: Ref<SwiperRef>,
) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const prevButtonRef = useRef<HTMLButtonElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);
  const [prevSlidesLength, setPrevSlidesLength] = useState(slides.length);
  const [isLocked, setIsLocked] = useState(false);

  const handlePrevClick = () => {
    onPrevClick?.();
    metadata?.elementVariant &&
      trackElementInteraction({
        elementId: 'previous_slide_button',
        elementVariant: metadata.elementVariant,
      });
  };

  const handleNextClick = () => {
    onNextClick?.();
    metadata?.elementVariant &&
      trackElementInteraction({
        elementId: 'next_slide_button',
        elementVariant: metadata.elementVariant,
      });
  };

  // recalculate slides if they are added or removed
  if (prevSlidesLength !== slides.length) {
    setPrevSlidesLength(slides.length);
    swiperInstance?.updateSlides();
  }

  return (
    <div className={styles.container} data-testid="slider">
      <Swiper
        className={styles.slider}
        cssMode
        modules={[Navigation]}
        navigation={{
          prevEl: prevButtonRef.current,
          nextEl: nextButtonRef.current,
        }}
        onLock={() => {
          setIsLocked(true);
        }}
        onSwiper={(swiper) => {
          setSwiperInstance(swiper);
        }}
        onUnlock={() => {
          setIsLocked(false);
        }}
        ref={ref}
        slidesPerGroupAuto
        slidesPerView="auto"
        {...options}
      >
        {slides.map((slide, index) => {
          return (
            <SwiperSlide className={clsx(styles.slide, slideClassName)} key={keyExtractor(slide)}>
              {renderSlide(slide, index)}
            </SwiperSlide>
          );
        })}
        {children}
      </Swiper>
      <div className={styles.navigation}>
        <button
          aria-label="Previous slide"
          className={clsx(styles.navigationButton, styles.prevButton)}
          data-testid="slider-prev-button"
          disabled={isLocked}
          onClick={handlePrevClick}
          ref={prevButtonRef}
        >
          <ChevronIcon color={COLORS.black} direction="left" size={IconSize.SIZE16} />
        </button>
        <button
          aria-label="Next slide"
          className={clsx(styles.navigationButton, styles.nextButton)}
          data-testid="slider-next-button"
          disabled={isLocked}
          onClick={handleNextClick}
          ref={nextButtonRef}
        >
          <ChevronIcon color={COLORS.black} direction="right" size={IconSize.SIZE16} />
        </button>
      </div>
    </div>
  );
};

SliderWithoutRef.displayName = 'Slider';

const Slider = forwardRef(SliderWithoutRef) as <SlideType>(
  props: SliderProps<SlideType> & { ref?: Ref<SwiperRef> },
) => ReturnType<typeof SliderWithoutRef>;

export default Slider;
